<template>
  <div class="offer-stock">
    <div class="text-nowrap">
      {{ $t("products.table.inStock") }}:
      <hover-popover class="offer-stock__value" :class="{ 'offer-stock__value--active': hasItems }" :disabled="!hasItems" popover-class="offer-stock-tooltip" @show="loadStock">
        <b>{{ offer.inStock == null ? "–" : offer.inStock }}</b>
        <template #content>
          <div v-if="isSet" class="offer-stock-tooltip__hint">{{ $t("offers.stock.setHint") }}</div>
          <div v-for="s in offer.itemStocks" :key="s.productId" class="offer-stock-tooltip__product">
            <div class="offer-stock-tooltip__head">
              <b>{{ s.sku }}</b>
              <span v-if="isSet"> · {{ s.quantity }}&times; {{ $t("offers.stock.perOffer") }}</span>
              · {{ $t("products.table.inStock") }} {{ s.inStock }}
              <span v-if="isSet"> → {{ sets(s.inStock, s.quantity) }} {{ $t("offers.stock.offers") }}</span>
            </div>
            <div v-if="stock[s.sku] == null" class="offer-stock-tooltip__muted">{{ $t("products.inflow.loading") }}</div>
            <div v-else-if="!stock[s.sku].length" class="offer-stock-tooltip__muted">{{ $t("wms.inventoryOverviewTooltip.noData") }}</div>
            <table v-else class="offer-stock-table">
              <tbody>
                <tr v-for="(r, i) in stock[s.sku]" :key="i">
                  <td>{{ r.inventoryName }}</td>
                  <td>{{ r.date ? formatDate(r.date) : "–" }}</td>
                  <td class="text-right">{{ r.inventory || 0 }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </hover-popover>
    </div>
    <div class="text-nowrap">
      {{ $t("products.table.inFlow") }}:
      <hover-popover class="offer-stock__value" :class="{ 'offer-stock__value--active': hasInflow }" :disabled="!hasInflow" popover-class="offer-stock-tooltip" @show="loadInflow">
        {{ offer.inFlow ? offer.inFlow : "–" }}
        <template #content>
          <div v-if="isSet" class="offer-stock-tooltip__hint">{{ $t("offers.stock.setHint") }}</div>
          <div v-for="s in offer.itemStocks" :key="s.productId" class="offer-stock-tooltip__product">
            <div class="offer-stock-tooltip__head">
              <b>{{ s.sku }}</b>
              <span v-if="isSet"> · {{ s.quantity }}&times; {{ $t("offers.stock.perOffer") }}</span>
              · {{ $t("products.table.inFlow") }} {{ s.inFlow }}
              <span v-if="isSet"> → {{ sets(s.inFlow, s.quantity) }} {{ $t("offers.stock.offers") }}</span>
            </div>
            <template v-if="s.inFlow > 0">
              <div v-if="inflow[s.productId] == null" class="offer-stock-tooltip__muted">{{ $t("products.inflow.loading") }}</div>
              <table v-else-if="inflow[s.productId].length" class="offer-stock-table">
                <thead>
                  <tr>
                    <th>{{ $t("products.inflow.container") }}</th>
                    <th>{{ $t("products.inflow.arrival") }}</th>
                    <th class="text-right">{{ $t("products.inflow.quantity") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="i in inflow[s.productId]" :key="i.shipmentRecordId">
                    <td :title="i.shipmentRecordName">{{ i.containerId || "–" }}</td>
                    <td>{{ i.arrivalDate ? formatDate(i.arrivalDate) : "–" }}</td>
                    <td class="text-right">{{ i.quantity }}</td>
                  </tr>
                </tbody>
              </table>
            </template>
          </div>
        </template>
      </hover-popover>
    </div>
  </div>
</template>

<script>
import axiosFactory from "@/services/axios.factory";
import HoverPopover from "@/components/HoverPopover.vue";

/**
 * Bestand und Zulauf eines Angebots. Bei Produktsets der kleinste gemeinsame Bestand (wie viele komplette Angebote);
 * der Mouseover zeigt je enthaltenem Produkt die Lagerplaetze bzw. die Container im Zulauf wie in der Produktuebersicht.
 */
export default {
  name: "OfferStockCell",
  components: { HoverPopover },
  props: {
    offer: { type: Object, required: true },
  },
  data() {
    return { stock: {}, inflow: {} };
  },
  computed: {
    hasItems() {
      return (this.offer.itemStocks || []).length > 0;
    },
    hasInflow() {
      return (this.offer.itemStocks || []).some((s) => s.inFlow > 0);
    },
    isSet() {
      const items = this.offer.itemStocks || [];
      return items.length > 1 || items.some((s) => s.quantity > 1);
    },
  },
  methods: {
    sets(pieces, quantity) {
      return quantity > 0 ? Math.floor(Math.max(0, pieces) / quantity) : 0;
    },
    loadStock() {
      (this.offer.itemStocks || []).forEach((s) => {
        // null = wird geladen; einmal je SKU, auch bei wiederholtem Mouseover
        if (s.sku in this.stock) return;
        this.$set(this.stock, s.sku, null);
        this.$store
          .dispatch("warehouses/getWarehouseInventoriesBySku", { sku: s.sku })
          .then((rows) => this.$set(this.stock, s.sku, (Array.isArray(rows) ? rows : []).filter((r) => (r.inventory || 0) !== 0)))
          .catch(() => this.$set(this.stock, s.sku, []));
      });
    },
    loadInflow() {
      (this.offer.itemStocks || []).forEach((s) => {
        if (!(s.inFlow > 0) || s.productId in this.inflow) return;
        this.$set(this.inflow, s.productId, null);
        axiosFactory()
          .get(`/shipping_data/products/${s.productId}/inflow`)
          .then((res) => this.$set(this.inflow, s.productId, (res.data && res.data.data) || []))
          .catch(() => this.$set(this.inflow, s.productId, []));
      });
    },
    formatDate(value) {
      return new Date(value).toLocaleDateString(this.$i18n.locale);
    },
  },
};
</script>

<style scoped>
.offer-stock__value--active {
  border-bottom: 1px dotted #999;
  cursor: default;
}
</style>

<style>
.offer-stock-tooltip.md-tooltip {
  height: auto;
  max-width: none;
  padding: 6px 8px;
  text-align: left;
}
.offer-stock-tooltip__hint {
  font-size: 11px;
  opacity: 0.85;
  margin-bottom: 4px;
  white-space: normal;
  max-width: 320px;
}
.offer-stock-tooltip__product + .offer-stock-tooltip__product {
  margin-top: 6px;
}
.offer-stock-tooltip__head {
  font-size: 12px;
}
.offer-stock-tooltip__muted {
  font-size: 11px;
  opacity: 0.8;
}
.offer-stock-table {
  border-collapse: collapse;
  font-size: 12px;
}
.offer-stock-table th,
.offer-stock-table td {
  padding: 1px 8px;
  text-align: left;
  white-space: nowrap;
}
.offer-stock-table .text-right {
  text-align: right;
}
</style>
